/**
 * photo-scale.js — миллиметры из фотографии по эталону в кадре.
 *
 * Общий примитив: им пользуются /mole-tracker (диаметр родинки) и
 * /crack-monitor (раскрытие трещины), и по той же причине им должен
 * пользоваться любой следующий инструмент, которому нужен настоящий размер, а
 * не «стало вроде больше».
 *
 * ── Почему это отдельный файл, а не копия в каждом туле ────────────────────
 * Таблица эталонов — это ФАКТЫ (диаметр монеты, длинная сторона банковской
 * карты по ISO/IEC 7810 ID-1). Две копии фактов расходятся молча: кто-то
 * поправит диаметр рубля в одном месте, и два инструмента начнут выдавать
 * разные миллиметры для одной и той же фотографии. Разошедшаяся копия при этом
 * не падает — она выдаёт правдоподобное неверное число, а его никто не
 * перепроверяет.
 *
 * ── Главное ограничение, которое обязан знать вызывающий ──────────────────
 * Масштаб верен ТОЛЬКО в плоскости эталона. Предмет, лежащий на сантиметр
 * ближе к объективу при съёмке с двадцати сантиметров, даёт ошибку около пяти
 * процентов — а это как раз та величина, ради которой затевается наблюдение
 * во времени. Поэтому `warnDepth` возвращает эту оценку явным числом, а не
 * оставляет её в примечании мелким шрифтом.
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) { module.exports = api; }
    if (root) { root.PhotoScale = api; }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
'use strict';

/* Размеры в миллиметрах. Монеты — по спецификациям эмитентов, карта — по
   ISO/IEC 7810 ID-1 (85,60 × 53,98 мм). */
var REFERENCES = {
    coin_ru_10:   25.0,
    coin_ru_5:    25.0,
    coin_ru_1:    20.5,
    coin_eur_1:   23.25,
    coin_eur_2:   25.75,
    coin_us_q:    24.26,
    coin_us_1c:   19.05,
    coin_gbp_1:   23.43,
    card:         85.6,
    card_short:   53.98,
    ruler_10mm:   10.0,
    ruler_1cm:    10.0,
    ruler_50mm:   50.0,
    a4_short:     210.0,
    a4_long:      297.0
};

/** @returns мм на пиксель, либо null — но НИКОГДА не 1 «по умолчанию». */
function calibrate(refPx, refMm) {
    var mm = typeof refMm === 'string' ? REFERENCES[refMm] : refMm;
    if (!mm || !isFinite(mm) || mm <= 0) return null;
    if (!refPx || !isFinite(refPx) || refPx <= 0) return null;
    return mm / refPx;
}

function distancePx(p1, p2) {
    if (!p1 || !p2) return 0;
    return Math.hypot(p2.x - p1.x, p2.y - p1.y);
}

/** Длина отрезка в миллиметрах. */
function measure(p1, p2, mmPerPx) {
    if (!mmPerPx) return null;
    return Math.round(distancePx(p1, p2) * mmPerPx * 100) / 100;
}

/**
 * Оценка ошибки от того, что эталон и объект лежат в разных плоскостях.
 * Простая оптика: относительная ошибка ≈ Δглубины / расстояние до камеры.
 * @param depthMm насколько эталон ближе или дальше объекта
 * @param distanceMm расстояние съёмки
 */
function warnDepth(depthMm, distanceMm) {
    if (!distanceMm || distanceMm <= 0) return null;
    return Math.round(Math.abs(depthMm) / distanceMm * 1000) / 10;   // проценты
}

/**
 * Сравнимость двух съёмок. Разошедшиеся условия дают разницу освещения и
 * ракурса, а не изменения объекта, и это надо называть вслух, а не прятать.
 */
function comparability(a, b, opts) {
    opts = opts || {};
    if (!a || !b) return { ok: null, issues: [] };
    var issues = [];
    if (a.mmPerPx == null || b.mmPerPx == null) issues.push('not_calibrated');
    if (a.refKey && b.refKey && a.refKey !== b.refKey) issues.push('ref_changed');
    if (a.mmPerPx && b.mmPerPx) {
        var ratio = a.mmPerPx / b.mmPerPx;
        var tol = opts.distanceTolerance || 1.67;
        if (ratio < 1 / tol || ratio > tol) issues.push('distance_changed');
    }
    if (a.flash !== b.flash) issues.push('flash_changed');
    return { ok: issues.length === 0, issues: issues };
}

return {
    REFERENCES: REFERENCES,
    calibrate: calibrate,
    measure: measure,
    distancePx: distancePx,
    warnDepth: warnDepth,
    comparability: comparability
};
});
